import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useService() {
  return useContext(Context);
}

const initState = {
  list: [],
  listSub: [],
  selected: null,
  selectedfile: null,
  serviceNames: [],
  servicesBySite: {},
  count: 0,
  page: 0,
  perPage: 10,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };
    case "set_list_service_names":
      return { ...state, serviceNames: [...action.payload] };
    case "set_listSubCompany":
      return { ...state, listSub: [...action.payload] };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "set_services_by_site":
      return {
        ...state,
        servicesBySite: {
          ...state.servicesBySite,
          [action.payload.siteId]: action.payload.services || [],
        },
      };
    case "set_count":
      return { ...state, count: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p,
      );
      return { ...state, list: modified };
    case "delete":
      const filtered = state.list.filter((p) => p.id !== action.payload);
      return { ...state, list: filtered };
    case "status":
      return { ...state, status: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };

    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function ServiceProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();

  const fetchList = useCallback(
    async (zid) => {
      let headersData = {
        pageNumber: 1,
        pageSize: 10,
      };
      dispatch({ type: "status", payload: "fetching" });
      const resData = await req(
        `SiteZoneService/List/${zid}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data || [] });
    },
    [req],
  );

  const fetchServiceNames = useCallback(
    async (siteId, memberShip) => {
      dispatch({ type: "status", payload: "fetching" });
      let servicesNamesHeadersData = {};
      if (memberShip) servicesNamesHeadersData.memberShip = memberShip;
      const resData = await req(
        `SiteZoneService/Names/${siteId}`,
        null,
        {},
        true,
        servicesNamesHeadersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({
        type: "set_services_by_site",
        payload: {
          siteId: siteId,
          services: resData?.data || [],
        },
      });
    },
    [req],
  );

  const fetchOne = useCallback(
    (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        try {
          const resData = await req(
            `SiteZoneService/Info/${id}`,
            null,
            {},
            true,
          );
          dispatch({ type: "set_selected", payload: resData.data });
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
    },
    [req],
  );

  const edit = useCallback(
    async (data, id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `SiteZoneService/Update/${id}`,
            data,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "edit", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  const createService = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        const newData = { ...data };
        try {
          const resData = await req(
            `SiteZoneService/Add`,
            newData,
            { method: "POST" },
            true,
          );
          // dispatch({ type: "add", payload: resData.data });
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  // const removeService = useCallback(
  //   async (id) => {
  //     return new Promise(async (resolve, reject) => {
  //       try {
  //         const resData = await req(
  //           `SiteZoneService/Delete/${id}/`,
  //           {},
  //           { method: "DELETE" },
  //           true,
  //         );
  //         dispatch({ type: "status", payload: "idle" });
  //         dispatch({ type: "deleteService", payload: id });
  //         resolve(resData);
  //       } catch (error) {
  //         reject(error);
  //       }
  //     });
  //   },
  //   [req],
  // );

  const remove = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          dispatch({ type: "status", payload: `deleting ${id}` });
          const resData = await req(
            `SiteZoneService/Delete/${id}/`,
            {},
            { method: "DELETE" },
            true,
          );
          dispatch({ type: "status", payload: "idle" });
          dispatch({ type: "delete", payload: id });
          resolve(resData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req],
  );
  return (
    <Context.Provider
      value={{
        state,
        fetchList,
        fetchOne,
        dispatch,
        createService,
        edit,
        remove,
        fetchServiceNames,
      }}
    >
      {children}
    </Context.Provider>
  );
}
